import clsx from "clsx";
import { FC, Fragment } from "react";

import type { ScoreEvent } from "@tsmono/inspect-common/types";
import { RecordTree, RenderedText } from "@tsmono/inspect-components/content";
import { formatDateTime } from "@tsmono/util";

import { EventPanel } from "./event/EventPanel";
import styles from "./ScoreEventView.module.css";
import { ScoreValue } from "./ScoreValue";
import { EventNode } from "./types";

interface ScoreEventViewProps {
  eventNode: EventNode<ScoreEvent>;
  className?: string;
}

export const ScoreEventView: FC<ScoreEventViewProps> = ({
  eventNode,
  className,
}) => {
  const event = eventNode.event;
  const resolvedTarget = event.target
    ? Array.isArray(event.target)
      ? event.target.join("\n")
      : event.target
    : undefined;

  return (
    <EventPanel
      eventNodeId={eventNode.id}
      title={(event.intermediate ? "Intermediate " : "") + "Score"}
      className={clsx(className, "text-size-small")}
      subTitle={
        event.timestamp ? formatDateTime(new Date(event.timestamp)) : undefined
      }
      icon="scorer"
    >
      <div data-name="Explanation" className={clsx(styles.explanation)}>
        {event.target ? (
          <Fragment>
            <div className={clsx(styles.separator)}></div>
            <div className={"text-style-label"}>Target</div>
            <div>
              <RenderedText markdown={resolvedTarget || ""} />
            </div>
          </Fragment>
        ) : (
          ""
        )}
        <div className={clsx(styles.separator)}></div>
        <div className={"text-style-label"}>Answer</div>
        <div className={clsx(styles.wrappingContent)}>
          <RenderedText markdown={event.score.answer || ""} />
        </div>
        <div className={clsx(styles.separator)}></div>
        <div className={"text-style-label"}>Explanation</div>
        <div className={clsx(styles.wrappingContent)}>
          <RenderedText markdown={event.score.explanation || ""} />
        </div>
        {event.score.reason ? (
          <Fragment>
            <div className={clsx(styles.separator)}></div>
            <div className={"text-style-label"}>Reason</div>
            {/* reason is a categorical literal (refusal, grader_failed, ...) or
                a short custom string — render verbatim, not as markdown. */}
            <div className={clsx(styles.wrappingContent)}>
              {event.score.reason}
            </div>
          </Fragment>
        ) : null}
        <div className={clsx(styles.separator)}></div>
        <div className={"text-style-label"}>Score</div>
        <ScoreValue score={event.score.value} />
        <div className={clsx(styles.separator)}></div>
      </div>
      {event.score.metadata ? (
        <div data-name="Metadata">
          <RecordTree
            id={`${eventNode.id}-score-metadata`}
            record={event.score.metadata}
            className={styles.metadata}
            defaultExpandLevel={0}
            copyButton={true}
          />
        </div>
      ) : undefined}
    </EventPanel>
  );
};
